import * as React from 'react';
import * as AgGrid from '@ag-grid-community/core';

import { parseOverTimeTree, TreeRowData } from 'src/utils/Tree/OvertimeTreeHandler';
import { isNil, get, isEqual, last, isEmpty } from 'lodash';

import AgGridHeaderBreakClass from 'src/utils/Style/AgGridThemeBreakHeaders';
import { classes, style } from 'typestyle';
import {
  makeGridContainerStyle,
  gridListPairStyle,
  makeGridContainerStyleSpecWidth,
} from 'src/components/ConfigurableGrid/ConfigurableGrid.styles';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { OvertimeCellRendererParams } from './OvertimeView.types';
import { NestedOvertimeViewDefn, NestedStyleOvertimeViewDefn } from 'configuration-schemas/viewdefns/viewdefn';
import { BasicViewItem } from 'configuration-schemas/viewdefns/general';
import { ExportOptions } from 'src/common-ui/components/DataGrid/DataGrid';
import { GetMainMenuItemsParams, ColumnResizedEvent } from '@ag-grid-community/core';
import ExtendedDataGrid from 'src/components/ExtendedDataGrid/ExtendedDataGrid';
import { generateColDefs } from './OvertimeView.utils';
import { FrameworkComponents } from 'src/utils/Component/AgGrid/AgConfigParse';
import { multiHeaderDecorate } from 'src/common-ui/components/DataGrid/NestedHeader';
import {
  frameworkComponents,
  nonFrameworkComponents,
} from 'src/components/ConfigurableGrid/EditableGrid/EditableGrid.subcomponents';
import { Option } from 'src/components/Configure/ConfigureModal';
import coalesce from 'src/utils/Functions/Coalesce';

export interface OvertimeViewProps {
  loaded: boolean;
  aggBys: string[];
  treeData: BasicPivotItem[];
  viewConfig: NestedOvertimeViewDefn | NestedStyleOvertimeViewDefn;
  search?: string;
  companionOpen?: boolean;
  hasCompanion?: boolean;
  exportOptions?: ExportOptions | undefined;
  rowHeight?: number;
  transposedSelections?: Option[];
}

interface OvertimeViewState {
  columnDefs: AgGrid.ColDef[];
  rowData: TreeRowData[];
  aggBys?: string[];
  metrics?: string[];
  defaultColDef: AgGrid.ColDef;
  groupDefaultExpanded: -1;
  getDataPath: (data: TreeRowData) => string[];
  autoGroupColumnDef: AgGrid.ColDef;
  components: unknown;
  frameworkComponents: FrameworkComponents;
}

const removeCheckMargin = style({
  $nest: {
    '.ag-group-checkbox': {
      display: 'none',
    },
  },
});

const bottomHeader = style({
  $nest: {
    '.ag-header-cell > :not(.group-child).ag-cell-label-container': {
      top: 0,
    },
    '.ag-header-cell-label > .ag-header-icon': {
      height: 'auto',
    },
  },
});

const headerName = style({
  paddingLeft: 10,
  cursor: 'pointer',
  $nest: {
    ['&.level-1']: {
      fontWeight: 'bold',
      paddingLeft: 0,
    },
    ['&.indent']: {
      paddingLeft: 45,
    },
    ['&.bold']: {
      fontWeight: 'bold',
    },
    ['&.italic']: {
      fontStyle: 'italic',
    },
  },
});

function getMenuItems(params: GetMainMenuItemsParams) {
  return params.defaultItems.filter((item) => item !== 'autoSizeAll');
}

export class NestedHeaderCellRenderer extends React.Component<OvertimeCellRendererParams> {
  constructor(props: OvertimeCellRendererParams) {
    super(props);
  }

  render() {
    const data = this.props.data;
    let textName = data?.name || data?.measureId;

    if (data) {
      const level = data?.path.length;
      const configClasses = data?.classes?.join(' ');
      const classes = `${headerName} level-${level} ${configClasses}`;
      return <span className={classes}>{textName}</span>;
    }
    textName = this.props.getValue && this.props.getValue();
    return <span>{textName}</span>;
  }
}

export class OvertimeView extends React.Component<OvertimeViewProps, OvertimeViewState> {
  gridApi: AgGrid.GridApi | null | undefined;
  constructor(props: OvertimeViewProps) {
    super(props);
    this.state = {
      columnDefs: [],
      rowData: [],
      defaultColDef: {
        sortable: false,
        resizable: true,
        filter: true,
        width: 150,
      },
      components: {},
      groupDefaultExpanded: -1,
      getDataPath: function (data: TreeRowData) {
        return data.path;
      },
      frameworkComponents: {
        ...frameworkComponents,
        headerCellRenderer: NestedHeaderCellRenderer,
      },
      // TODO: This should probably come from viewdefn >.>
      autoGroupColumnDef: {
        headerName: 'Name',
        width: 250,
        field: 'name',
        pinned: true,
        cellRendererParams: {
          checkbox: false,
          innerRenderer: 'headerCellRenderer',
          suppressCount: true,
        },
      },
    };
  }

  parseTreeData = async () => {
    const { aggBys, viewConfig, treeData, search, transposedSelections } = this.props;
    const metrics = viewConfig?.columns?.map((i) => {
      const col = i as Record<string, unknown>;
      const formatter = typeof col['renderer'] === 'string' ? col['renderer'] : undefined;
      const classes = Array.isArray(col['classes']) ? (col['classes'] as string[]) : [];
      return {
        ...i,
        key: i.dataIndex,
        name: i.text ?? '',
        formatter,
        classes,
      };
    });

    if (isNil(aggBys) || isNil(metrics) || isEmpty(treeData)) {
      return;
    }

    // transposed levels if configured
    const transposedDefLevels = transposedSelections?.map((selection) => selection.colDef);
    const transposedDataLevels = transposedSelections?.map((selection) => selection.dataIndex);

    const colDefLevels = coalesce(transposedDefLevels, get(viewConfig, 'timeLevels.colDef'), [
      'month',
      'member:week:description',
    ]) as string[];
    const dataLevels = coalesce(transposedDataLevels, get(viewConfig, 'timeLevels.data'), [
      'month',
      'week',
    ]) as string[];
    // the tree level at which to retrieve metric values
    const metricsTimeLevel = last(dataLevels) ?? '';
    const columnDefs = generateColDefs(colDefLevels, metricsTimeLevel, treeData, this.props.viewConfig.classes); // add metrics time level
    const rowData = parseOverTimeTree(treeData, aggBys, metrics, metricsTimeLevel, search);
    this.setState(
      {
        rowData,
        columnDefs,
        aggBys,
      },
      () => {
        if (this.gridApi) {
          this.gridApi.updateGridOptions({ columnDefs: multiHeaderDecorate(this.state.columnDefs) });
          this.gridApi.refreshCells({ force: true });
        }
      },
    );
  };
  componentDidMount(): void {
    if (this.gridApi) {
      this.parseTreeData();
    }
  }
  componentDidUpdate(prevProps: OvertimeViewProps) {
    if (
      !isEqual(prevProps.treeData, this.props.treeData) ||
      !isEqual(prevProps.search, this.props.search) ||
      !isEqual(prevProps.viewConfig, this.props.viewConfig) ||
      !isEqual(prevProps.transposedSelections, this.props.transposedSelections) ||
      !isEqual(prevProps.loaded, this.props.loaded)
    ) {
      if (!this.props.loaded) {
        this.setState({
          rowData: [],
        });
      } else if (this.gridApi) {
        this.parseTreeData();
      }
    }
  }

  onGridReady = (params: AgGrid.GridReadyEvent) => {
    this.gridApi = params.api;
    this.parseTreeData();
  };

  render() {
    const containerClass = this.props.hasCompanion
      ? makeGridContainerStyle(!this.props.companionOpen)
      : makeGridContainerStyleSpecWidth(0);
    const groupBolding = style({
      $nest: {
        '.ag-row-group': {
          fontWeight: 'bold',
        },
        '.ag-row-group-indent-0': {
          display: 'flex',
        },
        '.ag-row-group-indent-1': {
          display: 'flex',
        },
      },
    });
    return (
      <div
        id="myGrid"
        className={classes(
          'ag-theme-material',
          'data-grid',
          'double-header',
          AgGridHeaderBreakClass,
          removeCheckMargin,
          containerClass,
          bottomHeader,
          groupBolding,
        )}
      >
        <div className={gridListPairStyle}>
          <ExtendedDataGrid
            columnDefs={this.state.columnDefs}
            data={this.state.rowData}
            rowHeight={this.props.rowHeight}
            onGridReady={this.onGridReady}
            frameworkComponents={this.state.frameworkComponents}
            nonFrameworkComponents={nonFrameworkComponents}
            loaded={this.props.loaded}
            exportOptions={{
              customHeader: !isNil(this.props.exportOptions) ? this.props.exportOptions.customHeader : '',
              fileName: !isNil(this.props.exportOptions) ? this.props.exportOptions.fileName : '',
            }}
            extraAgGridProps={{
              defaultColDef: this.state.defaultColDef,
              treeData: true,
              animateRows: true,
              groupDefaultExpanded: this.state.groupDefaultExpanded,
              getDataPath: this.state.getDataPath,
              autoGroupColumnDef: this.state.autoGroupColumnDef,
              getMainMenuItems: getMenuItems,
              suppressMovableColumns: true,
              onColumnResized: (event: ColumnResizedEvent) => {
                if (event.finished && event.api) {
                  event.api.redrawRows();
                }
              },
            }}
          />
        </div>
      </div>
    );
  }
}
